import { useState } from "react";
import { useParams } from "react-router-dom";
import { AppBar, Tag, Btn, BtnLink } from "../components/index.js";
import { Icon } from "../components/Icon.js";
import { useSurface } from "../lib/useSurface.js";

/** Timeline fine-tune — scrub to pick any frame as the representative keyframe. */
export function Timeline() {
  useSurface("field");
  const { id = "draft" } = useParams();
  const [pos, setPos] = useState(22); // % along the track
  const tc = `00:${String(Math.round((pos / 100) * 32)).padStart(2, "0")}`;

  return (
    <div className="shell">
      <AppBar back={`/picker/${id}`} compactBrand>
        <Tag>手順 01 のフレーム微調整</Tag>
      </AppBar>

      <main className="wrap" style={{ padding: "20px 24px 110px" }}>
        <div className="kicker reveal">FINE-TUNE — 任意フレームをスクラブで選ぶ</div>
        <h1 className="display t-lg reveal d1" style={{ margin: "8px 0 16px" }}>
          ぴったりの<span className="hl">1コマ</span>を。
        </h1>

        <div className="player reveal d1" style={playerStyle}>
          <div className="frame" style={{ width: "34%", border: 0, background: "none" }}>
            <div className="subj" />
          </div>
          <span className="cap" style={{ position: "absolute", top: 12, right: 12, background: "var(--hi)", color: "#10130a", fontFamily: "var(--font-mono)", fontWeight: 700, padding: "3px 8px", borderRadius: 6 }}>
            {tc}
          </span>
        </div>

        <div className="scrub reveal d2" style={{ marginTop: 16 }}>
          <div className="track" style={trackStyle}>
            <div className="seg-band" style={{ ...band, left: 0, width: "40%" }}>
              <b style={bandB}>STEP 01 · 00:00–00:13</b>
            </div>
            <div className="seg-band" style={{ ...band, left: "40%", width: "60%", borderRight: 0 }}>
              <b style={bandB}>STEP 02 · 00:13–00:32</b>
            </div>
            <div className="kf" style={{ ...kf, left: "22%" }} />
            <div className="kf" style={{ ...kf, left: "31%", opacity: 0.45 }} />
            <div className="kf" style={{ ...kf, left: "13%", opacity: 0.45 }} />
            <div className="play" style={{ position: "absolute", top: 0, bottom: 0, width: 2, background: "#fff", left: `${pos}%` }} />
          </div>
          <label className="sr-only" htmlFor="scrub">
            再生位置
          </label>
          <input id="scrub" type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))} style={{ width: "100%", marginTop: 12, accentColor: "var(--hi)" }} />
          <div className="spread" style={{ marginTop: 10 }}>
            <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
              00:00
            </span>
            <span className="mono" style={{ fontSize: 12, color: "var(--hi)", display: "inline-flex", alignItems: "center", gap: 2 }}>
              選択中: {tc}（AI候補<Icon name="star" size={13} />）
            </span>
            <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
              00:32
            </span>
          </div>
        </div>

        <div className="row reveal d3" style={{ marginTop: 16, flexWrap: "wrap" }}>
          <Btn icon={<Icon name="arrow-left" size={16} />} onClick={() => setPos(Math.max(0, pos - 2))}>
            1コマ戻す
          </Btn>
          <Btn onClick={() => setPos(Math.min(100, pos + 2))}>
            1コマ進める <Icon name="arrow-right" size={16} />
          </Btn>
          <Btn icon={<Icon name="frame" size={16} />} disabled aria-disabled="true" title="準備中">
            ここを代表フレームに
          </Btn>
          <span className="grow" />
          <Btn variant="ghost" disabled aria-disabled="true" title="準備中">
            手順の境界も調整
          </Btn>
        </div>
      </main>

      <div className="actionbar">
        <span className="grow">
          <span className="mono" style={{ fontSize: 12, color: "var(--muted)" }}>
            変更は自動保存
          </span>
        </span>
        <BtnLink to={`/preview/${id}`} variant="hi" lg>
          確定してプレビューへ
        </BtnLink>
      </div>
    </div>
  );
}

const playerStyle = {
  aspectRatio: "16/9",
  borderRadius: 16,
  overflow: "hidden",
  position: "relative",
  background: "radial-gradient(120% 90% at 50% 30%,#23262f,#0a0b0e)",
  display: "grid",
  placeItems: "center",
  border: "1px solid var(--line)",
} as const;
const trackStyle = {
  position: "relative",
  height: 64,
  borderRadius: 12,
  background: "var(--surface2)",
  border: "1px solid var(--line)",
  overflow: "hidden",
} as const;
const band = { position: "absolute", top: 0, bottom: 0, borderRight: "1px dashed var(--line-2)", display: "flex", alignItems: "flex-start" } as const;
const bandB = { fontFamily: "var(--font-mono)", fontSize: 10, color: "var(--muted)", padding: "4px 6px" } as const;
const kf = { position: "absolute", top: 6, bottom: 6, width: 3, background: "var(--hi)", borderRadius: 2 } as const;
